import * as Cookies from 'js-cookie';

/**
 * Our custom cognito cookie storage object.
 *
 * Cognito requires a STATIC class, so it must be configured
 * statically via CookieStorage.configure(data);
 *
 */
export default class CookieStorage {
  static domain;

  static path = '/';

  static expires = 30;

  static secure = true;

  static sameSite = null;

  /**
   * Note: deviceTracking doesn't acutally change anything in the CookieStorage system at
   * the moment because currently the AWS Amplify Auth component will always generate
   * deviceIds and keys. *Sigh. Leaving it here in case it changes in the future.
   */
  static deviceTracking = true;

  /**
   * Configures the CookieStorage static
   *
   * @param {object} data options.
   * @param {string} data.domain Cookies domain (mandatory).
   * @param {string} data.path Cookies path (default: '/')
   * @param {integer} data.expires Cookie expiration (in days, default: 365)
   * @param {boolean} data.secure Cookie secure flag (default: true)
   * @param {string} data.sameSite Cookie request behaviour (default: null)
   * @param {boolean} data.deviceTracking Device tracking enabled in cognito (default: true)
   */
  static configure(data) {
    if (data.domain) {
      this.domain = data.domain;
    } else if (this.domain === '') {
      throw new Error('The domain of cookieStorage can not be undefined.');
    }

    if (data.path) {
      this.path = data.path;
    }

    if (Object.prototype.hasOwnProperty.call(data, 'expires')) {
      this.expires = data.expires;
    }

    if (Object.prototype.hasOwnProperty.call(data, 'secure')) {
      this.secure = data.secure;
    }

    if (Object.prototype.hasOwnProperty.call(data, 'sameSite')) {
      if (!['strict', 'lax', 'none'].includes(data.sameSite)) {
        throw new Error(
          'The sameSite value of cookieStorage must be "lax", "strict" or "none".',
        );
      }
      if (data.sameSite === 'none' && !this.secure) {
        throw new Error(
          'sameSite = None requires the Secure attribute in latest browser versions.',
        );
      }
      this.sameSite = data.sameSite;
    }

    if (Object.prototype.hasOwnProperty.call(data, 'deviceTracking')) {
      this.deviceTracking = data.deviceTracking === true;
    }
  }

  /**
   * This is used to set a specific item in storage
   * @param {string} key - the key for the item
   * @param {object} value - the value
   * @returns {string} value that was set
   */
  static setItem(key, value) {
    if (!this.shouldSave(key)) {
      return value;
    }
    switch (this.getStorageMechanism(key)) {
      case 'localStorage':
        // eslint-disable-next-line no-undef
        return localStorage.setItem(key, value);
      case 'cookie':
      default:
        Cookies.set(this.trimKey(key), value, this.getOptionsForKey(key));
        return Cookies.get(key);
    }
  }

  /**
   * This is used to get a specific key from storage
   * @param {string} key - the key for the item
   * @returns {string} the data item
   */
  static getItem(key) {
    switch (this.getStorageMechanism(key)) {
      case 'localStorage':
        // eslint-disable-next-line no-undef
        return localStorage.getItem(key);
      case 'cookie':
      default:
        return Cookies.get(this.trimKey(key));
    }
  }

  /**
   * This is used to remove an item from storage
   * @param {string} key - the key being set
   * @returns {string} value - value that was deleted
   */
  static removeItem(key) {
    switch (this.getStorageMechanism(key)) {
      case 'localStorage':
        // eslint-disable-next-line no-undef
        return localStorage.removeItem(key);
      case 'cookie':
      default:
        return Cookies.remove(this.trimKey(key), this.getOptionsForKey(key));
    }
  }

  static getOptionsForKey(key) {
    const options = {
      path: this.path,
      expires: this.expires,
      domain: this.domain,
      secure: this.secure,
    };
    if (this.sameSite) {
      options.sameSite = this.sameSite;
    }
    const index = key.lastIndexOf('.');
    let newKey = key;
    if (index > 0) {
      newKey = key.slice(index + 1);
    }
    switch (newKey) {
      case 'accessToken':
        options.expires = 1; // 1 day
        break;
      case 'refreshToken':
      case 'idToken':
      default:
        break;
    }
    return options;
  }

  /**
   * This is used to clear the storage
   * @returns {string} nothing
   */
  // eslint-disable-next-line class-methods-use-this
  static clear() {
    const cookies = Cookies.get();
    let index;
    // eslint-disable-next-line no-plusplus
    for (index = 0; index < cookies.length; ++index) {
      Cookies.remove(cookies[index]);
    }
    return {};
  }

  static trimKey(key) {
    const index = key.lastIndexOf('.');
    let prefix = '';
    if (index > 0) {
      prefix = this.hashCode(key.slice(0, index + 1));
    }
    const newKey = key.slice(index + 1);
    return prefix + newKey;
  }

  static shouldSave(key) {
    const index = key.lastIndexOf('.');
    const newKey = key.slice(index + 1);

    switch (newKey) {
      case 'clockDrift':
        return false;
      // turns out that we still need to store these even if we specify that deviceTracking is off
      // because the default amplify UI components still use them to generate an auth token.
      case 'randomPasswordKey':
      case 'deviceKey':
      case 'deviceGroupKey':
      case 'LastAuthUser':
      default:
        return true;
    }
  }

  /**
   * Put some items into localStorage, the others into cookies
   */
  static getStorageMechanism(key) {
    const index = key.lastIndexOf('.');
    let newKey = key;
    if (index > 0) {
      newKey = key.slice(index + 1);
    }
    switch (newKey) {
      case 'userData':
      case 'amplify-signin-with-hostedUI':
        return 'localStorage';
      // these items are for device tracking
      case 'randomPasswordKey':
      case 'deviceKey':
      case 'deviceGroupKey':
      case 'LastAuthUser':
      default:
        return 'cookie';
    }
  }

  static hashCode(stringToHash) {
    let hash = 0;
    if (stringToHash.length === 0) {
      return hash;
    }
    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < stringToHash.length; i++) {
      const char = stringToHash.charCodeAt(i);
      // eslint-disable-next-line no-bitwise
      hash = ((hash << 5) - hash) + char;
      // eslint-disable-next-line no-bitwise
      hash &= hash; // Convert to 32bit integer
    }
    return hash;
  }
}
